import { Component } from "react";
import PromineoDataGrid from "components/common/controls/grid/PromineoDataGrid";
import { Column, IDataGridOptions } from "devextreme-react/data-grid";
import { connect } from "react-redux";
import { RootState, AppDispatch } from "store/store";
import AdminProductLicense from "interfaces/response/AdminProductLicense";
import ManageLicenseGridActionHeader from "./ManageLicenseGridActionHeader";
import LicenseEditorPopup from "./LicenseEditorPopup";
import LicenseGridActions from "./LicenseGridActions";
import { EditorMode } from "shared/enums/EditorModeEnum";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import Loader from "components/common/controls/Loader";
import { deleteProductLicense } from "store/slices/ProductLicenseManagementSlice";

interface Props {
  dataSource?: AdminProductLicense[];
  dispatch?: AppDispatch;
}

interface State {
  isEditLicensePopupVisible: boolean;
  isDeleteLicensePopupVisible: boolean;
  isLoading: boolean;
  slectedRowData?: AdminProductLicense;
}

export class ProductLicenseGrid extends Component<Props, State> {
  state: Readonly<State> = {
    isEditLicensePopupVisible: false,
    isDeleteLicensePopupVisible: false,
    isLoading: false,
    slectedRowData: undefined,
  };

  handleEditLicenseClick = (data: AdminProductLicense) => {
    this.setState({
      isEditLicensePopupVisible: true,
      slectedRowData: data,
    });
  };

  handleEditLicensePopupClose = () => {
    this.setState({
      isEditLicensePopupVisible: false,
    });
  };

  handleDeleteLicenseClick = (data: AdminProductLicense) => {
    this.setState({
      isDeleteLicensePopupVisible: true,
      slectedRowData: data,
    });
  };

  handleDeleteLicensePopupClose = () => {
    this.setState({
      isDeleteLicensePopupVisible: false,
    });
  };

  handleDeleteConfirm = () => {
    this.setState({ isLoading: true, isDeleteLicensePopupVisible: false });
    this.props.dispatch!(
      deleteProductLicense({ licenseId: this.state.slectedRowData!.id })
    ).then(() => {
      this.setState({ isLoading: false });
    });
  };

  render() {
    const dataGridOptions: IDataGridOptions = {
      dataSource: this.props.dataSource,
      keyExpr: "id",
      height: "500px",
    };

    return (
      <>
        <div className="flex mt-5 mb-10">
          <ManageLicenseGridActionHeader />
        </div>
        <div>
          <PromineoDataGrid
            dataGridOptions={dataGridOptions}
            noDataText="No License found.
            (Click ‘Add License’ to Add new license)"
          >
            <Column dataField="name" caption="Name" width="13%" />
            <Column
              dataField="isCustomizableLicense"
              caption="Customizable"
              width="8%"
            />
            <Column
              dataField="actionValidationDurationUnitText"
              caption="Duration Unit"
              width="12%"
            />
            <Column dataField="price" caption="Price" width="8%" />
            <Column
              dataField="isAutomaticPurchaseAllowed"
              caption="Automatic Purchase Allowed"
              width="12%"
            />
            <Column
              dataField="billingIntervalText"
              caption="Billing Interval"
              width="10%"
            />
            <Column dataField="stripeId" caption="Stripe Id" width="10%" />
            <Column
              dataField="isDefaultFreeLocalLicense"
              caption="Default Free Local License"
              width="12%"
            />
            <Column
              caption="Actions"
              cssClass="w-16 lg:w-48 border"
              cellRender={(licenseData: any) => (
                <LicenseGridActions
                  onEditClick={() => {
                    this.handleEditLicenseClick(licenseData.data);
                  }}
                  onDeleteClick={() => {
                    this.handleDeleteLicenseClick(licenseData.data);
                  }}
                />
              )}
            />
          </PromineoDataGrid>
        </div>
        {this.state.isEditLicensePopupVisible && (
          <LicenseEditorPopup
            onClose={this.handleEditLicensePopupClose}
            mode={EditorMode.Edit}
            license={this.state.slectedRowData}
          />
        )}
        {this.state.isDeleteLicensePopupVisible && (
          <DeleteConfirmationDialog
            handleConfirm={this.handleDeleteConfirm}
            onClose={this.handleDeleteLicensePopupClose}
            content={"Do you want to delete this license?"}
          />
        )}
        <Loader isVisible={this.state.isLoading} />
      </>
    );
  }
}

const mapStateToProps = (state: RootState, props: Props) => {
  return {
    dataSource: state.productLicenseManagementData.productLicense,
    ...props,
  };
};

export default connect(mapStateToProps)(ProductLicenseGrid);
